import { Check, Palette } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Tip } from "@/components/ui/overlays";
import { cn } from "@/lib/utils";
import { useTheme } from "@/stores/theme";

import { LOGO_THEMES, themeById } from "./themes";

/** A compact brand-theme picker for the top bar: a swatch button that opens a
 * list of palettes. Selecting one recolours the inline logo (animated) and the
 * favicon instantly. */
export function ThemeSwitcher({ className }: { className?: string }) {
  const id = useTheme((s) => s.id);
  const setTheme = useTheme((s) => s.setTheme);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const active = themeById(id);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <Tip label={`Brand theme — ${active.label}`}>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Brand theme"
          aria-expanded={open}
          className="grid h-8 w-8 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Palette className="h-4 w-4" />
        </button>
      </Tip>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-1.5 w-[184px] rounded-md border border-border bg-muted/95 p-1 shadow-deep backdrop-blur animate-rise-in">
          <p className="px-2 pb-1 pt-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Brand theme
          </p>
          {LOGO_THEMES.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTheme(t.id);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-accent",
                t.id === id ? "text-foreground" : "text-muted-foreground",
              )}
            >
              <span className="flex shrink-0 items-center -space-x-1">
                {[t.base, t.gold, t.accent].map((c, i) => (
                  <span
                    key={i}
                    className="h-3.5 w-3.5 rounded-full border border-border/60"
                    style={{ background: c }}
                  />
                ))}
              </span>
              <span className="flex-1">{t.label}</span>
              {t.id === id && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
